import { Tooltip } from 'antd'
import { memo, useContext, useMemo } from 'react'

import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import { useUserInfoStore } from '@/stores/user/userInfoStore'

import cn from '../../StockImportsTab/StockImportsTab.module.scss'
import {
  ColumnRole,
  type ColumnRoleOption,
  getExcelColumnName,
  ParsedRow,
  ParsedSheet,
} from '../UrlExcelMapper/UrlExcelMapper.types'
import { SegmentColumnsContext } from './SegmentColumnsContext'
import SegmentPriceOption from './SegmentPriceOption'

const SEGMENT_PRICE_VALUE = '__segment_price__'

interface UrlExcelTableProps {
  activeSheet: ParsedSheet
  roles: readonly ColumnRoleOption[]
  roleByColumn: Record<number, ColumnRole>
  startRowIndex?: number
  endRowIndex?: number
  onColumnRoleChange: (columnIndex: number, role?: ColumnRole) => void
  onRowSelect: (rowNumber: number) => void
  onStartRowSelect: (rowNumber: number) => void
}

interface UrlExcelHeaderCellProps {
  columnIndex: number
  role?: ColumnRole
  roles: readonly ColumnRoleOption[]
  roleByColumn: Record<number, ColumnRole>
  isOrganisationSelected: boolean
  onColumnRoleChange: (columnIndex: number, role?: ColumnRole) => void
}

interface UrlExcelRowProps {
  row: ParsedRow
  maxColumns: number
  isRangeStart: boolean
  isRangeEnd: boolean
  isReadableRow: boolean
  onRowSelect: (rowNumber: number) => void
  onStartRowSelect: (rowNumber: number) => void
}

const UrlExcelHeaderCell = memo(function UrlExcelHeaderCell({
  columnIndex,
  role,
  roles,
  roleByColumn,
  isOrganisationSelected,
  onColumnRoleChange,
}: UrlExcelHeaderCellProps) {
  const segmentColumns = useContext(SegmentColumnsContext)
  const hasSegmentOption = segmentColumns !== null
  const options = useMemo(
    () => [
      ...roles.map((currentRole) => ({
        value: currentRole.value,
        label: currentRole.label,
        disabled: Object.entries(roleByColumn).some(
          ([mappedColumn, mappedRole]) =>
            mappedRole === currentRole.value &&
            Number(mappedColumn) !== columnIndex
        ),
      })),
      ...(hasSegmentOption
        ? [
            {
              value: SEGMENT_PRICE_VALUE,
              label: 'Цена по сегменту',
              disabled: true,
              className: cn.urlExcelSegmentItem,
            },
          ]
        : []),
    ],
    [columnIndex, roles, roleByColumn, hasSegmentOption]
  )
  const segmentIds = segmentColumns?.segmentIdsByColumn[columnIndex] ?? []
  const segmentLabel = segmentColumns?.segments
    .filter((segment) => segmentIds.includes(segment.id))
    .map((segment) => segment.label)
    .join(', ')

  const handleRoleChange = (nextRole?: ColumnRole) => {
    if (nextRole && segmentIds.length > 0)
      segmentColumns?.onChange(columnIndex, [])
    onColumnRoleChange(columnIndex, nextRole)
  }

  const handleSegmentsChange = (nextIds: string[]) => {
    if (role && nextIds.length > 0) onColumnRoleChange(columnIndex, undefined)
    segmentColumns?.onChange(columnIndex, nextIds)
  }

  return (
    <th>
      <div className={cn.urlExcelColumnHeader}>
        <span>{getExcelColumnName(columnIndex)}</span>
        <HoverElement>
          <AtomSelect
            openByHover={false}
            outlined
            allowClear
            isOrganisationSelected={isOrganisationSelected}
            value={segmentIds.length > 0 ? SEGMENT_PRICE_VALUE : role}
            options={options}
            optionRender={(option) =>
              option.value === SEGMENT_PRICE_VALUE && segmentColumns ? (
                <SegmentPriceOption
                  columnIndex={columnIndex}
                  segmentColumns={segmentColumns}
                  isOrganisationSelected={isOrganisationSelected}
                  onSelectionChange={handleSegmentsChange}
                />
              ) : (
                <span
                  className={cn.urlExcelRoleOption}
                  title={String(option.label ?? '')}
                >
                  {option.label}
                </span>
              )
            }
            labelRender={(option) => {
              const label =
                option.value === SEGMENT_PRICE_VALUE
                  ? `Цена: ${segmentLabel}`
                  : String(option.label ?? '')
              return (
                <span className={cn.urlExcelSelectedRole} title={label}>
                  {label}
                </span>
              )
            }}
            popupMatchSelectWidth={250}
            handleChange={(value) =>
              handleRoleChange(
                roles.find((item) => item.value === value)?.value
              )
            }
            handleClear={() => {
              segmentColumns?.onChange(columnIndex, [])
              onColumnRoleChange(columnIndex, undefined)
            }}
            placeholder="Поле"
          />
        </HoverElement>
      </div>
    </th>
  )
})

const UrlExcelRow = memo(function UrlExcelRow({
  row,
  maxColumns,
  isRangeStart,
  isRangeEnd,
  isReadableRow,
  onRowSelect,
  onStartRowSelect,
}: UrlExcelRowProps) {
  const cells = useMemo(
    () =>
      Array.from({ length: maxColumns }, (_, columnIndex) => (
        <td key={columnIndex}>
          <span>{row.cells[columnIndex] ?? ''}</span>
        </td>
      )),
    [maxColumns, row.cells]
  )

  return (
    <tr
      className={[
        cn.urlExcelDataRow,
        isReadableRow ? cn.urlExcelSelectedRow : '',
        isRangeStart ? cn.urlExcelRangeStart : '',
        isRangeEnd ? cn.urlExcelRangeEnd : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onRowSelect(row.rowNumber)}
    >
      <td className={cn.urlExcelRowNumber}>
        <Tooltip title="Выбрать границу диапазона">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onStartRowSelect(row.rowNumber)
            }}
          >
            {row.rowNumber}
          </button>
        </Tooltip>
      </td>
      {cells}
    </tr>
  )
})

function UrlExcelTable({
  activeSheet,
  roles,
  roleByColumn,
  startRowIndex,
  endRowIndex,
  onColumnRoleChange,
  onRowSelect,
  onStartRowSelect,
}: UrlExcelTableProps) {
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const maxColumns = activeSheet.maxColumns
  const columnIndexes = useMemo(
    () => Array.from({ length: maxColumns }, (_, columnIndex) => columnIndex),
    [maxColumns]
  )

  return (
    <div className={cn.urlExcelTableWrap}>
      <table className={cn.urlExcelTable}>
        <thead>
          <tr>
            <th className={cn.urlExcelCornerCell}>Строка</th>
            {columnIndexes.map((columnIndex) => (
              <UrlExcelHeaderCell
                key={columnIndex}
                columnIndex={columnIndex}
                role={roleByColumn[columnIndex]}
                roles={roles}
                roleByColumn={roleByColumn}
                isOrganisationSelected={isOrganisationSelected}
                onColumnRoleChange={onColumnRoleChange}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {activeSheet.rows.map((row) => {
            const isRangeStart = startRowIndex === row.rowNumber
            const isRangeEnd = endRowIndex === row.rowNumber
            const isReadableRow =
              typeof startRowIndex === 'number' &&
              row.rowNumber >= startRowIndex &&
              (typeof endRowIndex !== 'number' || row.rowNumber <= endRowIndex)

            return (
              <UrlExcelRow
                key={`${activeSheet.name}-${row.rowNumber}`}
                row={row}
                maxColumns={maxColumns}
                isRangeStart={isRangeStart}
                isRangeEnd={isRangeEnd}
                isReadableRow={isReadableRow}
                onRowSelect={onRowSelect}
                onStartRowSelect={onStartRowSelect}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default memo(UrlExcelTable)
